import { Box, Button, Divider } from "@mui/material";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import LoadingModal from "../../components/LoadingModal";
import useArrayObject from "../../hooks/useArrayObject";
import useInputObject from "../../hooks/useInputObject";
import { getNextSalesRecordNo } from "../../http/api/SalesRecordAPI";
import { delay, getHyphenedDate } from "../../utils/utils";
import SALES_RECORD_TYPE from "../../utils/variable";
import Header from "./Header";
import SalesDetail from "./SalesDetail";
import SalesResults from "./SalesResults";
import UpsertConfirmModal from "./UpsertConfirmModal";
import useSalesRecordAPI from "./useSalesRecordAPI";
import { resultsIsEmpty } from "./util/InitialSalesRecord";
import useVarData from "./util/useVarData";
import { isNullEmpty } from "../../utils/validation";

function handleKeyDown(event) {
  if (event.key === "Insert" && event.location === 0) {
    //Insert Key Press
    event.preventDefault();
    document.getElementById("salesRecordUpdateButton").focus();
    document.getElementById("salesRecordUpdateButton").click();
  }
  // For test
  else if (event.key === "Enter" && event.ctrlKey) {
    // Ctrl + Enter Key Press
    event.preventDefault();
    document.getElementById("salesRecordUpdateButton").focus();
    document.getElementById("salesRecordUpdateButton").click();
  }
}

export default function SalesRecordEntry() {
  const setting = useInputObject({
    salesRecordType: SALES_RECORD_TYPE.LOCAL,
    invoiceIsDisabled: false,
  });
  const { salesRecordType } = useParams();
  // console.log(getCount());

  const { state } = useLocation();

  const {
    invoice,
    getSalesRecordByNo,
    updateSalesRecord,
    updateStock,
    setupNewSalesRecord,
    isLoading: isLoadingSalesRecord,
    insertedInvoice,
    renderState,
  } = useSalesRecordAPI();
  const salesRecord = useInputObject(invoice.Value.salesRecordInfo);
  const salesDetails = useArrayObject(invoice.Value.listSalesDetailInfo);
  const salesResultses = useArrayObject(invoice.Value.listSalesResultsInfo);
  const { ranks, productClasses, packings } = useVarData();
  const historyItems = useArrayObject([]);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  useEffect(() => {
    const today = getHyphenedDate();
    const arrivalDate = invoice.Value.salesRecordInfo.arrivalDate;

    const isPastInvoice = arrivalDate < today;

    const isPurchacePriceFilled = invoice.Value.listSalesDetailInfo.every(
      (detailInfo) => detailInfo.isNew || Boolean(detailInfo.purchacePrice),
    );

    const invoiceIsDisabled =
      isPastInvoice &&
      !invoice.Value.salesRecordInfo.isNewInvoice &&
      isPurchacePriceFilled;
    setting.update("invoiceIsDisabled", invoiceIsDisabled);
  }, [
    invoice.Value.salesRecordInfo.salesRecordNo,
    invoice.Value.salesRecordInfo.arrivalDate,
    invoice.Value.salesRecordInfo.isNewInvoice,
  ]);

  useEffect(() => {
    if (setting.Value.salesRecordType === SALES_RECORD_TYPE.STOCK) return;
    setting.update(
      "salesRecordType",
      parseInt(invoice.Value.salesRecordInfo.salesRecordTypeCd) ||
        SALES_RECORD_TYPE.LOCAL,
    );
  }, [invoice.Value.salesRecordInfo.salesRecordTypeCd]);

  useEffect(() => {
    const currentSalesRecordNo = invoice.Value.salesRecordInfo.salesRecordNo;
    if (
      setting.Value.salesRecordType === SALES_RECORD_TYPE.TRAVEL &&
      isNullEmpty(currentSalesRecordNo)
    ) {
      //旅販売原票新規の場合は自動採番販売原票Noを作成する
      getNextSalesRecordNo().then(async (res) => {
        salesRecord.update("salesRecordNo", res);
        //販売原票番号設定した後全選択状況設定する
        await delay(500);
        document.getElementById("header-2").select();
      });
    } else {
      //他・在の場合は販売原票Noフィールドにフォーカスする
      (async () => {
        await delay(500);
        const el = document.getElementById("header-2");
        if (el) el.select();
      })();
    }
  }, [setting.Value.salesRecordType]);

  useEffect(() => {
    setting.update(
      "salesRecordType",
      parseInt(salesRecordType) ||
        insertedInvoice.Value.salesRecordTypeCd ||
        SALES_RECORD_TYPE.LOCAL,
    );
    setupNewSalesRecord({ salesRecordTypeCd: salesRecordType });
  }, [salesRecordType]);

  useEffect(() => {
    (async () => {
      if (state) {
        const initSalesRecord = {
          ...salesRecord,
          Value: {
            ...salesRecord.Value,
            salesRecordNo: state.salesRecordNo,
          },
        };

        getSalesRecordByNo(initSalesRecord, salesRecordType);
        historyItems.setValue([]);
      }
    })();

    document.getElementById(`header-2`).focus();
  }, []);

  useEffect(() => {
    salesRecord.setValue(invoice.Value.salesRecordInfo);
    salesDetails.setValue(invoice.Value.listSalesDetailInfo);
    salesResultses.setValue(invoice.Value.listSalesResultsInfo);
  }, [invoice.Value]);

  const changeHistoryItem = (itemName, value, valueName = null) => {
    const index = historyItems.Value.findIndex(
      (item) => item.itemName == itemName,
    );

    if (index >= 0) {
      historyItems.updateIndexProp(index, "value", value);
      if (valueName !== null) {
        historyItems.updateIndexProp(index, "valueName", valueName);
      }
    } else {
      historyItems.push({ itemName, value, valueName });
    }
  };

  const changeHistoryItemsWithArray = (historyItemsToUpdate) => {
    let newHistoryItems = [...historyItems.Value];

    for (const historyItem of historyItemsToUpdate) {
      const index = newHistoryItems.findIndex(
        (item) => item.itemName == historyItem.itemName,
      );

      if (index >= 0) {
        newHistoryItems[index] = {
          itemName: historyItem.itemName,
          value: historyItem.value,
          valueName: historyItem.valueName,
        };
      } else {
        newHistoryItems.push({
          itemName: historyItem.itemName,
          value: historyItem.value,
          valueName: historyItem.valueName,
        });
      }
    }

    historyItems.setValue(newHistoryItems);
  };

  const confirmUpdateSalesRecord = useCallback(async () => {
    setOpenConfirmModal(false);

    const newInvoice = {
      salesRecord,
      salesDetails,
      salesResultses,
      historyItems,
    };
    const onUpdated = () => {
      historyItems.setValue([]);
    };

    if (salesRecordType == SALES_RECORD_TYPE.STOCK) {
      await updateStock(newInvoice, onUpdated);
    } else {
      await updateSalesRecord(newInvoice, onUpdated);
    }
  }, [
    salesRecord.Value,
    salesDetails.Value,
    salesResultses.Value,
    historyItems,
    historyItems.setValue,
    salesRecordType,
  ]);

  const handleUpdateSalesRecord = useCallback(async () => {
    if (openConfirmModal) return;
    document.activeElement.blur();
    const isAllResultsNull = salesResultses.Value.every((item) =>
      resultsIsEmpty(item),
    );
    if (isAllResultsNull) {
      await confirmUpdateSalesRecord();
    } else {
      setOpenConfirmModal(true);
    }
  }, [
    openConfirmModal,
    salesDetails.Value,
    salesResultses.Value,
    confirmUpdateSalesRecord,
  ]);

  const hasSalesDetail = useMemo(() => {
    return salesDetails.Value.some((detail) => detail?.quantity > 0);
  }, [salesDetails.Value]);
  return (
    <Fragment>
      <Box sx={{ width: "100%", height: "100vh" }} onKeyDown={handleKeyDown}>
        {/* 販売原票 */}
        <Header
          salesRecord={salesRecord}
          salesDetails={salesDetails}
          apiSalesRecord={invoice.Value.salesRecordInfo}
          insertedInvoice={insertedInvoice}
          getSalesRecordByNo={getSalesRecordByNo}
          setting={setting}
          renderState={renderState}
          changeHistoryItem={changeHistoryItem}
          changeHistoryItemsWithArray={changeHistoryItemsWithArray}
          setHistoryItems={historyItems.setValue}
        />
        {/* 販売原票明細 */}
        <Divider sx={{ fontSize: 25, my: 2 }}>販売原票明細</Divider>
        <SalesDetail
          salesDetails={salesDetails}
          apiSalesDetails={invoice.Value.listSalesDetailInfo}
          setting={setting}
          insertedInvoice={insertedInvoice}
          ranks={ranks}
          productClasses={productClasses}
          packings={packings}
          renderState={renderState}
          changeHistoryItem={changeHistoryItem}
          changeHistoryItemsWithArray={changeHistoryItemsWithArray}
        />
        {/* 販売実績 */}
        <Divider sx={{ fontSize: 25, my: 2 }}>販売実績</Divider>
        <SalesResults
          salesResultses={salesResultses}
          salesDetails={salesDetails}
          hasSalesDetail={hasSalesDetail}
          apiSalesResults={invoice.Value.listSalesResultsInfo}
          renderState={renderState}
          setting={setting}
          changeHistoryItem={changeHistoryItem}
          changeHistoryItemsWithArray={changeHistoryItemsWithArray}
        />
        <Box display="flex" justifyContent="center" sx={{ paddingBlock: 5 }}>
          <Button
            // size="large"
            sx={{ width: 300, height: 50, fontSize: 20 }}
            variant="contained"
            // color="success"
            onClick={handleUpdateSalesRecord}
            id="salesRecordUpdateButton"
            disabled={
              setting.Value.salesRecordType !== SALES_RECORD_TYPE.STOCK &&
              setting.Value.invoiceIsDisabled
            }
          >
            登録
          </Button>
          {/* <Button
            onClick={(e) => {
              // console.log("invoice", invoice.Value);
              console.log("invoice", {
                header: salesRecord.Value,
                details: salesDetails.Value,
                results: salesResultses.Value,
              });
            }}
          >
            CheckState
          </Button> */}
        </Box>
        <LoadingModal
          open={isLoadingSalesRecord}
          label="取得中"
          backgroundOpacity={0.1}
        />
        {openConfirmModal && (
          <UpsertConfirmModal
            setOpen={setOpenConfirmModal}
            salesRecord={salesRecord}
            salesDetails={salesDetails}
            salesResultses={salesResultses}
            confirmAction={confirmUpdateSalesRecord}
            ranks={ranks}
            productClasses={productClasses}
            packings={packings}
          />
        )}
      </Box>
    </Fragment>
  );
}
